
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { UserTable } from "@/components/admin/users/UserTable";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageTitle } from "@/components/ui/PageTitle";
import { toast } from "sonner";

const RegisteredUsers = () => {
  const { data: owners, isLoading } = useQuery({
    queryKey: ["registered-users"],
    queryFn: async () => {
      try {
        // Verify if user is admin
        const { data: currentUserProfile } = await supabase
          .from("profiles")
          .select("user_type")
          .single();

        if (currentUserProfile?.user_type !== "admin") {
          throw new Error("Unauthorized access");
        }

        // Fetch all profiles with their associated owner data
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select(`
            *,
            owner_data (
              company_name,
              business_type_id
            )
          `);

        if (profilesError) {
          console.error("Error fetching profiles:", profilesError);
          throw profilesError;
        }

        // Get all subscriptions separately
        const { data: subscriptions, error: subsError } = await supabase
          .from('subscriptions')
          .select('*');

        if (subsError) {
          console.error("Error fetching subscriptions:", subsError);
          throw subsError;
        }

        // Combine the data
        const enrichedProfiles = profiles.map(profile => {
          const userSubscription = subscriptions?.find(sub => sub.owner_id === profile.id);
          return {
            ...profile,
            subscription_plan: userSubscription?.plan || 'free',
            product_limit: userSubscription?.product_limit || 3
          };
        });

        return enrichedProfiles;
      } catch (error: any) {
        console.error("Error:", error);
        toast.error("Error loading users");
        throw error;
      }
    },
  });

  const handleBlockUser = async (userId: string, isBlocked: boolean) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ is_blocked: isBlocked })
        .eq("id", userId);

      if (error) throw error;

      toast.success(
        isBlocked ? "User blocked successfully" : "User unblocked successfully"
      );
    } catch (error) {
      console.error("Error updating user status:", error);
      toast.error("Error updating status");
    }
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6 space-y-4">
        <PageTitle 
          title="Registered Users" 
          description="Manage all users registered on the platform"
        />
        
        <UserTable
          owners={owners || []}
          isLoading={isLoading}
          onBlockUser={handleBlockUser}
        />
      </div>
    </DashboardLayout>
  );
};

export default RegisteredUsers;
